import { Injectable, signal } from "@angular/core";

/**
 * Tracks WHICH price ids the user has touched since load/save, so the save
 * fan-out PATCHes only those prices (`PATCH /enrichment/price/:id` per id)
 * while `EnrichmentDirtyTracker` keeps answering the coarse "anything
 * unsaved?" question.
 *
 * Deliberately fed by explicit hooks at the only writers that touch
 * `__detail.prices` — `EnrichmentPricing.updateSelectedPrice`,
 * `EnrichmentShelf.updatePriceShelves` and
 * `EnrichmentSpecialPromotion.writeBack` — rather than by diffing model
 * emissions: the shelf writer `structuredClone`s the whole model, so
 * reference-diffing would mark every price dirty, and deep-equality diffing
 * is both expensive per keystroke and risky around the tri-state
 * `null`/`undefined` price-field semantics.
 *
 * Provided in `EnrichmentEditPackage`'s `providers` (NOT root) so the set
 * dies with the route — a fresh edit session always starts clean. Lifecycle:
 * the shell calls {@link clear} beside `dirty.beginLoad()` (reload / channel
 * switch) and `dirty.markDiscarded()`; the save coordinator calls
 * {@link clearDirty} per price PATCH that succeeds, so a partial failure
 * naturally leaves only the failed ids marked for Retry.
 */
@Injectable()
export class EnrichmentPriceDirtyStore {
  private readonly ids = signal<ReadonlySet<string>>(new Set());

  /** Read-only signal of the currently dirty price ids. */
  readonly dirtyIds = this.ids.asReadonly();

  /** Monotonic edit counter — bumped on EVERY {@link markDirty} call, even
   *  when the id is already in the set, because each call is a fresh edit
   *  signal from a writer. Never reset. The shell captures it when a section
   *  save starts and compares on completion to detect edits typed while the
   *  PATCH was in flight (see `SectionDirtyStore.editEpoch`). */
  private edits = 0;

  /** The same counter per price. The save coordinator captures each sent
   *  id's value and, on completion, commits the server's copy only for an
   *  id that did not move: one edited while its PATCH was in flight keeps
   *  the newer keystrokes on screen and stays dirty. */
  private readonly epochs = new Map<string, number>();

  /** Current edit epoch — see {@link edits}. */
  editEpoch(): number {
    return this.edits;
  }

  editEpochOf(id: string): number {
    return this.epochs.get(id) ?? 0;
  }

  isDirty(id: string): boolean {
    return this.ids().has(id);
  }

  markDirty(id: string): void {
    if (!id) return;
    this.edits++;
    this.epochs.set(id, this.editEpochOf(id) + 1);
    if (this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.add(id);
    this.ids.set(next);
  }

  clearDirty(id: string): void {
    if (!this.ids().has(id)) return;
    const next = new Set(this.ids());
    next.delete(id);
    this.ids.set(next);
  }

  clear(): void {
    if (this.ids().size === 0) return;
    this.ids.set(new Set());
  }
}
